import type { DocNode } from '@atlaskit/adf-schema';
import { reduce, traverse } from '@atlaskit/adf-utils/traverse';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

type StreamEvent = {
	adfResponse?: DocNode | DocNode['content'][number];
	cacheKey?: string;
	markdown?: string;
	modelInput?: object;
	type: string;
};

export interface Ramp {
	flushDelaySec: number;
	growthFactor: number;
	initialDelaySec: number;
	stepDelaySec: number;
}

export interface StreamFlowPacerConfig {
	backlogRampThreshold: number;
	initialCharsPerTick: number;
	maxCharsPerTick: number;
	ramp: Ramp;
	tickMs: number;
}

class Count {
	constructor(
		readonly chars: number,
		readonly length: number,
	) {}

	eq(other: Count): boolean {
		return this.chars === other.chars && this.length === other.length;
	}

	sub(other: Count): Count {
		return new Count(this.chars - other.chars, this.length - other.length);
	}

	gte(other: Count): boolean {
		return this.chars >= other.chars && this.length >= other.length;
	}
}

/**
 * TODO: EDITOR-1533 - Improve this class and make it more generic.
 */
export class StreamFlowPacer {
	readonly push = (evt: StreamEvent): void => {
		if (evt.type === 'stream') {
			this.streamEvt = evt;
		} else {
			this.terminalEvt = evt;
		}
	};

	constructor(private readonly cfg: StreamFlowPacerConfig) {
		this.charsPerTick = cfg.initialCharsPerTick;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	setEmit(emit: (evt: any) => void): void {
		this.emit = emit;
	}

	setTickMs(tickMs: number): void {
		this.cfg.tickMs = tickMs;
	}

	setCharsPerTick(charsPerTick: number): void {
		this.charsPerTick = charsPerTick;
	}

	setMaxCharsPerTick(maxCharsPerTick: number): void {
		this.cfg.maxCharsPerTick = maxCharsPerTick;
	}

	/**
	 * you need to call setEmit before calling start
	 *
	 * @example
	 * streamFlowPacer.setEmit();
	 * streamFlowPacer.start();
	 */
	start(): void {
		if (this.destroyed) {
			return;
		}

		if (this.emit) {
			this.timer = setInterval(() => this.tick(), this.cfg.tickMs);
		}
	}

	pause(): void {
		this.paused = true;
		this.stop();
	}

	resume(): void {
		this.paused = false;
		this.start();
	}

	destroy(): void {
		this.reset();
		this.destroyed = true;
	}

	onComplete(handler: () => void): void {
		this.onCompleteHandlers.push(handler);
	}

	reset(): void {
		this.stop();
		this.emit = null;
		this.streamEvt = null;
		this.terminalEvt = null;
		this.charsPerTick = this.cfg.initialCharsPerTick;
		this.delivered = new Count(0, 0);
		this.prevTickTime = 0;
		this.currTickTime = 0;
		this.ticksSinceRamp = 0;
		this.ramped = 0;
		this.paused = false;
		this.destroyed = false;
		this.onCompleteHandlers.length = 0;
	}

	isRunning(): boolean {
		return this.timer !== null;
	}

	private timer: ReturnType<typeof setInterval> | null = null;
	private streamEvt: StreamEvent | null = null;
	private terminalEvt: StreamEvent | null = null;
	private charsPerTick: number;
	private delivered: Count = new Count(0, 0);
	private prevTickTime = 0;
	private currTickTime = 0;
	private ticksSinceRamp = 0;
	private ramped = 0;
	private paused = false;
	private destroyed = false;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private emit: ((evt: any) => void) | null = null;
	private onCompleteHandlers: Array<() => void> = [];
	private processedSyncBlockLocalId: (string | null)[] = [];

	private stop() {
		if (this.timer) {
			clearInterval(this.timer);
			this.timer = null;
		}
	}

	private tick() {
		this.prevTickTime = this.currTickTime;
		this.currTickTime = Date.now();

		if (this.paused || !(this.streamEvt || this.terminalEvt)) {
			return;
		}

		this.maybeRamp();
		this.deliverChunk();
		this.maybeFlushTerminal();
	}

	private deliverChunk() {
		if (!this.streamEvt) {
			return;
		}

		const total = this.semanticLen(this.streamEvt);
		if (this.delivered.gte(total)) {
			return;
		}

		let slice: StreamEvent | null;

		if (
			this.prevTickTime > 0 &&
			this.currTickTime > 0 &&
			this.currTickTime - this.prevTickTime >= 3000
		) {
			// If it's been more than 3 seconds since last tick,
			// assume the tab is in background, so we flush everything
			slice = this.sliceStreamEvt(this.streamEvt, -1);
		} else if (this.delivered.chars >= total.chars && this.delivered.length < total.length) {
			// No more text chars can be found we should flush the entire latest response
			slice = this.sliceStreamEvt(this.streamEvt, -1);
		} else {
			slice = this.sliceStreamEvt(
				this.streamEvt,
				Math.min(this.delivered.chars + this.charsPerTick, total.chars),
			);
		}

		if (!slice) {
			return;
		}

		this.emit?.(slice);
		this.delivered = this.semanticLen(slice);

		if (this.delivered.eq(total) && !!this.onCompleteHandlers.length) {
			this.onCompleteHandlers.forEach((handler) => handler());
			this.onCompleteHandlers.length = 0;
		}
	}

	private canFlushTerminal(): boolean {
		if (!this.terminalEvt) {
			return false;
		}

		// For "complete" we must wait until every char is flushed
		// For mock, the final response can be less than the sum of previous responses, so backlog can be negative
		if (this.terminalEvt.type === 'complete' || this.terminalEvt.type === 'response too similar') {
			return this.backlog().chars <= 0;
		}

		// All other terminal kinds (error, abort, etc.) bypass the backlog
		return true;
	}

	private maybeFlushTerminal(): void {
		if (!this.canFlushTerminal()) {
			return;
		}

		if (this.terminalEvt) {
			this.emit?.(this.terminalEvt);
			this.destroy();
		}
	}

	private maybeRamp() {
		if (!this.shouldRamp()) {
			this.ticksSinceRamp++;
			return;
		}
		this.charsPerTick = Math.min(
			Math.ceil(this.charsPerTick * this.cfg.ramp.growthFactor),
			this.cfg.maxCharsPerTick,
		);
		this.ticksSinceRamp = 0;
		this.ramped++;
	}

	private shouldRamp() {
		const wait = this.secondsToTicks(
			this.getRampDelay(this.cfg.ramp, this.ramped, this.terminalEvt),
		);
		return (
			(this.backlog().chars > this.cfg.backlogRampThreshold ||
				this.terminalEvt?.type === 'response too similar') &&
			this.ticksSinceRamp >= wait &&
			this.charsPerTick < this.cfg.maxCharsPerTick
		);
	}

	private backlog() {
		if (!this.streamEvt) {
			return new Count(0, 0);
		}
		const result = this.semanticLen(this.streamEvt);
		return result.sub(this.delivered);
	}

	private getRampDelay(rampConfig: Ramp, rampedCnt: number, response?: StreamEvent | null): number {
		if (response?.type === 'response too similar') {
			return rampConfig.flushDelaySec;
		}
		return rampedCnt > 0 ? rampConfig.stepDelaySec : rampConfig.initialDelaySec;
	}

	private secondsToTicks(sec: number) {
		return (sec * 1000) / this.cfg.tickMs;
	}

	private semanticLen(evt: StreamEvent) {
		if (!!evt.markdown && typeof evt.markdown === 'string') {
			return new Count(evt.markdown.length, evt.markdown.length);
		}

		return this.adfTextLen(evt.adfResponse);
	}

	/**
	 *
	 * @param evt
	 * @param chars
	 * @returns
	 */
	private sliceStreamEvt(evt: StreamEvent, chars: number): StreamEvent | null {
		if (evt.adfResponse) {
			if (chars < 0) {
				return {
					...evt,
					adfResponse: expValEquals('platform_editor_aifc_sync_block_stream_fix', 'isEnabled', true)
						? this.filterIncompleteNodes(evt.adfResponse)
						: evt.adfResponse,
				};
			}
			return { ...evt, adfResponse: this.truncateAdf(evt.adfResponse, chars) };
		}
		if (evt.markdown?.length) {
			return { ...evt, markdown: evt.markdown.slice(0, chars < 0 ? undefined : chars) };
		}
		return null;
	}

	public adfTextLen(root: ADFEntity | null | undefined): Count {
		if (!root) {
			return new Count(0, 0);
		}

		const { chars, length } = reduce<{ chars: number; length: number }>(
			root,
			(result, node) =>
				node.type === 'text' && typeof node.text === 'string'
					? { chars: result.chars + node.text.length, length: result.length + node.text.length + 2 }
					: { chars: result.chars, length: result.length + 2 },
			{
				chars: 0,
				length: 0,
			},
		);
		return new Count(chars, length);
	}

	/**
	 * Sometimes the localID is a UUID with 36 characters (8-4-4-4-12 with dashes).
	 * Sometimes it's a 12 character string.
	 * So we validate the length and the presence of dashes.
	 */
	private static isValidLocalId(value: unknown): boolean {
		return (
			typeof value === 'string' && (value.includes('-') ? value.length >= 36 : value.length === 12)
		);
	}

	/**
	 * Lightweight filter for the flush path (chars < 0).
	 * Drops bodiedSyncBlock nodes with invalid localId.
	 * This is used to ensure the structure is not emitted until the localId is complete.
	 */
	private filterIncompleteNodes(root: ADFEntity): DocNode {
		return traverse(root, {
			bodiedSyncBlock: (node) => {
				if (node.type === 'bodiedSyncBlock') {
					if (!this.processedSyncBlockLocalId.includes(node.attrs?.localId)) {
						return false;
					}
					return;
				}
			},
		}) as DocNode;
	}

	private truncateAdf(root: ADFEntity, budget: number): DocNode {
		let remaining = Math.max(0, budget);
		/**
		 * Some older browsers like chrome 97 doesn't support structuredClone.
		 * So using JSON.parse(JSON.stringify()) as a fallback.
		 */
		const clone =
			typeof window !== 'undefined' && typeof window.structuredClone === 'function'
				? structuredClone(root)
				: JSON.parse(JSON.stringify(root));

		let lastTableRowLocalId: string;
		let remainingMergedRows = 1;

		return traverse(clone, {
			any: (node, parent) => {
				//Drop bodiedSyncBlock nodes with invalid localId.
				if (
					node.type === 'bodiedSyncBlock' &&
					expValEquals('platform_editor_aifc_sync_block_stream_fix', 'isEnabled', true)
				) {
					if (!this.processedSyncBlockLocalId.includes(node.attrs?.localId)) {
						//if it has an invalid local id, we mark it as pending
						if (!StreamFlowPacer.isValidLocalId(node.attrs?.localId)) {
							return false;
						} else {
							this.processedSyncBlockLocalId.push(node.attrs?.localId);
							return;
						}
						// we've seen this before, we are emitting it
					} else {
						return;
					}
				}

				if (node.type === 'tableCell' || node.type === 'tableHeader') {
					// merge information is stored inside cells, so we keep track of the largest rowspan we encounter
					remainingMergedRows = Math.max(remainingMergedRows, node.attrs?.rowspan || 1);
				}
				// We subtract the 'merged rows' count as we encounter rows
				if (remainingMergedRows > 0 && node.type === 'tableRow') {
					remainingMergedRows--;
				}

				if (node.type === 'tableRow') {
					/**
					 * Upon parsing a table row, we mark it as seen using the localId
					 * so we can check when descending into the node whether to keep the row structure
					 * or drop it entirely (if no chars are remaining when entering a table row)
					 */
					lastTableRowLocalId = node.attrs?.localId;
				}

				if (remaining === 0) {
					if (node.type === 'tableRow') {
						if (remainingMergedRows === 0) {
							return false;
						}
						return;
					}
					let ancestor: typeof parent | undefined = parent;
					while (ancestor) {
						if (ancestor?.node?.type === 'tableRow') {
							if (
								// if we're in the middle of a table row that has been parsed, we want to keep the rest of the cells
								ancestor.node?.attrs?.localId === lastTableRowLocalId ||
								// if there are still merged rows to be processed, we want to keep the following rows and cells too
								remainingMergedRows > 0
							) {
								if (node.type === 'tableCell' || node.type === 'tableHeader') {
									/**
									 * We return all cells in the current row that's being streamed to
									 * ensure the table row structure is maintained.
									 * We also keep table cell structures if there are still merged rows to be processed.
									 */
									return expValEquals(
										'platform_editor_ai_escape_early_for_unhealthy_node',
										'isEnabled',
										true,
									)
										? {
												...node,
												content: [
													{
														type: 'paragraph',
														content: [],
													},
												],
											}
										: undefined;
								}

								// keep empty paragraphs to make tableCell and tableHeader valid
								if (
									node.type === 'paragraph' &&
									node.content?.length === 0 &&
									expValEquals(
										'platform_editor_ai_escape_early_for_unhealthy_node',
										'isEnabled',
										true,
									)
								) {
									return;
								}

								return false;
							}
							return false;
						}
						ancestor = ancestor.parent;
					}
					return false;
				}

				if (node.type !== 'text' || typeof node.text !== 'string') {
					return;
				}

				if (node.text.length <= remaining) {
					remaining -= node.text.length;
					return;
				}
				const text = node.text.slice(0, remaining);
				remaining = 0;
				return { ...node, text };
			},
		}) as DocNode;
	}
}
